import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

// Define supported languages and RTL languages
export const SUPPORTED_LANGUAGES = ['en', 'ar'] as const;
export const RTL_LANGUAGES = ['ar'] as const;

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

export const isRTL = (language: string): boolean => {
  return RTL_LANGUAGES.includes(language as any);
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Language settings
    fallbackLng: 'en',
    debug: import.meta.env.DEV,
    supportedLngs: SUPPORTED_LANGUAGES,
    
    // Language detection options
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },

    // Backend configuration for loading translation files
    backend: {
      loadPath: '/lifeline/locales/{{lng}}/{{ns}}.json',
    },

    // Namespace configuration
    ns: [
      'common',
      'widgets',
      'pages',
      'forms',
      'notifications',
      'auth',
      'appointments', 
      'goals',
      'journal',
      'profile',
      'coach',
      'admin',
      'values',
      'validation'
    ],
    defaultNS: 'common',

    interpolation: {
      escapeValue: false, // React already does escaping
    },

    // React specific options
    react: {
      useSuspense: false, // We'll handle loading states ourselves
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'em'],
    },
  });

// Update document direction and lang attribute when language changes
i18n.on('languageChanged', (lng: string) => {
  const htmlElement = document.documentElement;
  
  // Update document language
  htmlElement.lang = lng;
  
  // Update document direction for RTL support
  if (isRTL(lng)) {
    htmlElement.dir = 'rtl';
    htmlElement.classList.add('rtl');
    htmlElement.classList.remove('ltr');
  } else {
    htmlElement.dir = 'ltr';
    htmlElement.classList.add('ltr');
    htmlElement.classList.remove('rtl');
  }
});

export default i18n;